import { Suspense, useEffect, useState } from "react"
import { Outlet } from "react-router-dom"
import { Sidebar } from "./Sidebar"
import { TopBar } from "./TopBar"
import { CommandPalette } from "@/components/CommandPalette"
import { TooltipProvider } from "@/components/ui/tooltip"
import { useLinks } from "@/components/AppSwitcher"
import { useAuthStore } from "@/store/auth"

const COLLAPSED_STORAGE_KEY = "rmnc-energie.sidebar.collapsed"

/** Energie-Akzent Gelb (#f1c40f, docs/SHELL_PORTIERUNG.md §4). */
const ENERGIE_ACCENT = "48 89% 50%"

function Fallback() {
  return <div className="p-6 text-sm text-muted-foreground">lädt…</div>
}

/**
 * Shell: EINE dunkle Plattform-Kopfzeile (TopBar, trägt Collapse-Burger,
 * AppSwitcher, Suche, EnvBadge, User) + linke Sidebar + hellem Content.
 * Bewusst KEINE zweite (helle) Header-Zeile — Korrektur ggü. der
 * Personal-Vorlage, die dort versehentlich zwei Kopfzeilen übereinander hatte.
 */
export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  })
  const [paletteOpen, setPaletteOpen] = useState(false)
  const links = useLinks()
  const hasOnlySeuAccess = useAuthStore((s) => s.hasOnlySeuAccess())

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, sidebarCollapsed ? "1" : "0")
    } catch {
      /* Privacy-Mode: Zustand bleibt in-memory */
    }
  }, [sidebarCollapsed])

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
        />

        <div className="flex flex-1 overflow-hidden">
          <Sidebar collapsed={sidebarCollapsed} accent={ENERGIE_ACCENT} />

          {/* Volle Breite wie Technik-Vorlage (kein mx-auto/max-w-Container) */}
          <main className="w-full flex-1 overflow-auto px-6 py-8">
            <Suspense fallback={<Fallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        links={links}
        hasOnlySeuAccess={hasOnlySeuAccess}
      />
    </TooltipProvider>
  )
}
